import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx, keyframes } from '@emotion/react';
import { reduceMotionAsPerUserPreference } from '@atlaskit/motion';
import { P300 } from '@atlaskit/theme/colors';
// NOTE:
// Pulse color "rgb(101, 84, 192)" derived from "colors.P300"
const baseShadow = `0 0 0 2px ${`var(--ds-border-discovery, ${P300})`}`;
const easing = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';
const pulseKeyframes = keyframes({
  '0%, 33%': {
    boxShadow: `${baseShadow}, 0 0 0 ${"var(--ds-border-discovery, rgba(101, 84, 192, 1))"}`
  },
  '66%, 100%': {
    boxShadow: `${baseShadow}, 0 0 0 10px rgba(101, 84, 192, 0.01)`
  }
});
// This is needed for unit tests
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const pulseKeyframesName = pulseKeyframes.name;
const animationStyles = css({
  animation: `${pulseKeyframes} 3000ms ${easing} infinite`,
  // Keep a purple boxshadow on the Pulse component if `prefers-reduced-motion`
  // is applied so we still have a functioning semantic affordance.
  boxShadow: baseShadow
});
const Base = ({
  children,
  bgColor,
  radius,
  style,
  testId,
  ...props
}) => jsx("div", _extends({
  "data-testid": testId
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
  ,
  style: {
    ...style,
    backgroundColor: bgColor,
    borderRadius: radius ? `${radius}px` : undefined
  }
  // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
}, props), children);

/**
 * __Target inner__
 *
 * Used to apply spotlight border and pulse to spotlight targets.
 *
 * @internal
 */
export const TargetInner = ({
  children,
  pulse,
  ...props
}) => jsx(Base
// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
, _extends({}, props, {
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
  css: [pulse && animationStyles, reduceMotionAsPerUserPreference]
}), children);
const targetOverlayStyles = css({
  width: '100%',
  height: '100%',
  position: 'absolute',
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  insetBlockStart: 0,
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  insetInlineStart: 0
});

/**
 * __Target overlay__
 *
 * Overlays a spotlight target, allowing for custom click events that are intended
 * only for onboarding.
 *
 * @internal
 */
export const TargetOverlay = props => jsx("div", _extends({}, props, {
  css: targetOverlayStyles
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
  ,
  style: {
    cursor: props.onClick ? 'pointer' : 'auto'
  }
}));

/**
 * __Spotlight pulse__
 *
 * A spotlight pulse draws attention to a new feature.
 *
 * - [Examples](https://atlassian.design/components/onboarding/examples)
 * - [Code](https://atlassian.design/components/onboarding/code)
 * - [Usage](https://atlassian.design/components/onboarding/usage)
 */
export const Pulse = ({
  children,
  pulse = true,
  testId,
  ...props
}) => jsx(Base
// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
, _extends({}, props, {
  css: [pulse && animationStyles,
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
  reduceMotionAsPerUserPreference],
  testId: testId
}), children);